import { createApi } from "@reduxjs/toolkit/dist/query/react";
import ApiService from "./ApiService";
import { baseQuery } from "./basequery";
import { isNull } from "lodash";

const servicePrefix = "/marketplace";

export async function apiExport(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/export/",
    method: "post",
    data,
  });
}

export async function apiListCategories() {
  return ApiService.fetchData({
    url: servicePrefix + "/category/",
    method: "get",
  });
}

export async function apiListProducts(params) {
  return ApiService.fetchData({
    url: servicePrefix + "/products/",
    params: { ...params },
    method: "get",
  });
}
export async function apiListUserProducts() {
  return ApiService.fetchData({
    url: servicePrefix + "/inventory/",
    method: "get",
  });
}

export async function apiGetProduct(id) {
  return ApiService.fetchData({
    url: servicePrefix + "/products/" + id + "/",
    method: "get",
  });
}

export async function apiGetProductFeedbacks(id) {
  return ApiService.fetchData({
    url: servicePrefix + "/feedback/" + id + "/",
    method: "get",
  });
}
export async function apiCreateProductFeedbacks(id, data) {
  return ApiService.fetchData({
    url: servicePrefix + "/feedback/" + id + "/",
    method: "post",
    data,
  });
}
export async function apiFilterProductsByCategory(data) {
  return ApiService.fetchData({
    url: servicePrefix + "/product-category/",
    method: "post",
    data,
  });
}

export const marketplaceService = createApi({
  reducerPath: "marketplace",
  baseQuery: baseQuery,
  tagTypes: ["products", "feedbacks", "inventory"],
  // refetchOnFocus: true,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: true,
  endpoints: (build) => ({
    getCategories: build.query({
      query: () => ({
        url: "",
        method: "get",
      }),
    }),

    getProducts: build.query({
      query: ({ name, ...params }) => ({
        url: isNull(name)
          ? `${servicePrefix}/products/`
          : `${servicePrefix}/search/`,
        method: "get",
        params: { name, ...params },
      }),
      providesTags: (result) =>
        result?.results
          ? [
              ...result?.results?.map?.(({ id }) => ({ type: "products", id })),
              { type: "products", id: "LIST" },
            ]
          : [{ type: "products", id: "LIST" }],
    }),

    createProductComment: build.mutation({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/feedback/${values?.id}/`,
        method: "post",
        data: { ...values },
      }),
      invalidatesTags: [{ type: "feedbacks", id: "LIST" }],
    }),

    getProduct: build.query({
      query: ({ id }) => ({
        url: `${servicePrefix}/products/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: "products", id }],
    }),

    getFeedbacks: build.query({
      query: ({ id }) => ({
        url: `${servicePrefix}/feedback/${id}/`,
      }),
      providesTags: (result) =>
        result?.results
          ? [
              ...result?.results?.map?.(({ id }) => ({
                type: "feedbacks",
                id,
              })),
              { type: "feedbacks", id: "LIST" },
            ]
          : [{ type: "feedbacks", id: "LIST" }],
    }),

    getCurrentUserProducts: build.query({
      query: ({ ...values }) => ({
        url: `${servicePrefix}/inventory/`,
        params: { ...values },
      }),
      providesTags: (data) =>
        data?.results
          ? [
              ...data?.results?.map?.(({ id }) => ({
                type: "inventory",
                id,
              })),
              { type: "inventory", id: "LIST" },
            ]
          : [{ type: "inventory", id: "LIST" }],
    }),

    deleteCurrentUserProduct: build.mutation({
      query: ({ id }) => ({
        url: `${servicePrefix}/inventory/${id}/`,
        method: "delete",
      }),
      invalidatesTags: [{ type: "inventory", id: "LIST" }],
    }),

    getTransactions: build.query({
      query: () => ({
        url: `${servicePrefix}/transactions/`,
      }),
    }),

    getWallet: build.query({
      query: () => ({
        url: `${servicePrefix}/wallet/`,
      }),
    }),

    fundWallet: build.mutation({
      query: ({ ...values }) => ({
        url: `/billing/fund/`,
        method: "post",
        data: { ...values },
      }),
    }),

    payout: build.mutation({
      query: ({ ...values }) => ({
        url: `/billing/payout/`,
        method: "post",
        data: { ...values },
      }),
    }),

    getCategories: build.query({
      query: () => ({
        url: "/marketplace/category/",
      }),
    }),

    getIndustries: build.query({
      query: () => ({
        url: `/marketplace/industry/`,
      }),
    }),

    postExport: build.mutation({
      query: ({ ...values }) => ({
        url: servicePrefix + "/export/",
        method: "post",
        data: { ...values },
      }),
    }),

    purchaseProduct: build.mutation({
      query: ({ product_id, ...values }) => ({
        url: `${servicePrefix}/purchase/`,
        method: "post",
        data: { product_id },
      }),
    }),

    patchProduct: build.mutation({
      query: ({ id, formdata }) => ({
        url: `${servicePrefix}/inventory/${id}/`,
        method: "patch",
        data: formdata,
      }),
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetProductQuery,
  useGetFeedbacksQuery,
  useCreateProductCommentMutation,
  useGetCurrentUserProductsQuery,
  useGetTransactionsQuery,
  useGetWalletQuery,
  useFundWalletMutation,
  useDeleteCurrentUserProductMutation,
  useGetCategoriesQuery,
  useGetIndustriesQuery,
  usePostExportMutation,
  usePayoutMutation,
  usePurchaseProductMutation,
  usePatchProductMutation,
} = marketplaceService;
